import { z } from 'zod';
import { BackendBaseEnvSchema } from './backendBase';

// Backend schema: logging/APM + common server/infra
// Used by both sync and async services
const BackendEnvSchema = BackendBaseEnvSchema.extend({
  LOCAL_LOGGING_ENABLED: z
    .string()
    .optional()
    .transform((v) => v === 'true'),
  // Logging / Elastic
  ELASTIC_CLOUD_ID: z.string(),
  ELASTIC_API_KEY: z.string(),
  ELASTIC_DATA_STREAM: z.string(),
  ELASTIC_APM_SERVER_URL: z.string().optional(),
  ELASTIC_APM_SECRET_TOKEN: z.string().optional(),

  // Outbound email / backend
  SENDGRID_API_KEY: z.string(),
  BETTER_AUTH_URL: z.string(),

  // Auth trusted origins - required for BetterAuth initialization
  TRUSTED_MOBILE_URL: z.string(),
  UI_WEB_URL: z.string(),
  REDIS_URL: z.string(),

  // Redis components
  REDIS_HOST: z.string().optional(),
  REDIS_PORT: z.string().optional(),
  REDIS_USERNAME: z.string().optional(),
  REDIS_PASSWORD: z.string().optional(),

  // Mixpanel analytics
  MIXPANEL_PROJECT_TOKEN: z.string().optional(),

  // Better uptime keys for prod and sandbox
  SYNC_BETTER_UPTIME_KEY: z.string().optional(),
  ASYNC_BETTER_UPTIME_KEY: z.string().optional(),

  PORT: z
    .string()
    .optional()
    .transform((v) => (v ? parseInt(v, 10) : undefined))
    .pipe(z.number().int().positive().optional()),

  // Ethereum Node Providers
  QUICKNODE_API_KEY_8453: z.string().optional(),
  QUICKNODE_API_KEY_43114: z.string().optional(),
  QUICKNODE_API_KEY_42161: z.string().optional(),
  QUICKNODE_API_KEY_137: z.string().optional(),
  QUICKNODE_API_KEY_10: z.string().optional(),
  QUICKNODE_API_KEY_1: z.string().optional(),
  ALCHEMY_API_KEY_2020: z.string().optional(),

  // Explorer API Keys
  ETHERSCAN_API_KEY: z.string(),

  // CoinGecko API (for token logos)
  COINGECKO_API_KEY: z.string(),

  // DefiLlama API (for token price fetching)
  DEFILLAMA_API_KEY: z.string(),

  // Smaug contract-deployment metadata (STAGE environment)
  TENDERLY_TESTNET_CHAIN_ID: z
    .string()
    .optional()
    .transform((v) => (v ? parseInt(v, 10) : undefined)),

  // Onchain event indexing parameters
  INDEXING_ADDRESS_CHUNK_SIZE: z
    .string()
    .default('250')
    .transform((v) => parseInt(v, 10)),
  INDEXING_MAX_BLOCK_RANGE: z
    .string()
    .default('10000')
    .transform((v) => parseInt(v, 10)),
  INDEXING_ITERATION_MAX_TIME_MS: z
    .string()
    .default('4000')
    .transform((v) => parseInt(v, 10)),
  INDEXING_ITERATION_MIN_TIME_MS: z
    .string()
    .default('400')
    .transform((v) => parseInt(v, 10)),
  INDEXING_REORG_REWIND_BLOCKS: z
    .string()
    .default('10')
    .transform((v) => parseInt(v, 10)),

  // Local-only setup helper: reset/redeploy local chain contracts during setupDevEnv
  RESET_LOCAL_CHAIN_ON_SETUP: z
    .string()
    .optional()
    .transform((v) => v === 'true'),

  // Guardian Lambda configuration (used by async service to invoke Lambda)
  GUARDIAN_LAMBDA_FUNCTION_NAME: z.string().optional(),
  AWS_REGION: z.string().default('us-east-2'),
});

type BackendEnv = z.infer<typeof BackendEnvSchema>;

export function getBackendEnvVariables(): BackendEnv {
  return BackendEnvSchema.parse(process.env);
}
